import { Flex, HoverCard } from "@mantine/core";
import dayjs               from "dayjs";
import dynamic             from "next/dynamic";
import { useRouter }       from "next/router";
import { useState }        from "react";
import { useTranslation }  from "react-i18next";

import { storeEvent }                     from "_analytics/index";
import { useGetDeviceType }               from "_common/hooks/global";
import { convertToSlug, shortenNumber }   from "_common/utils";
import { PrimaryButton, SecondaryButton } from "_components/atoms/button";
import Icon                               from "_components/atoms/icon";
import Image                              from "_components/atoms/image";
import Typography                         from "_components/atoms/typography";
import Chip                               from "_components/molecules/chip";
import { ProChip }                        from "_components/molecules/chip/presentation";
import FollowUser                         from "_components/molecules/followUser";
import SocialMediaButtons                 from "_components/molecules/socialMediaButtons";
import ProChannelDropdown                 from "_components/organisms/proChannelDropdown";
import useStyles                          from "_styles/templates/userProfileHeader";

const ReportFollowModal = dynamic ( () => import ( "_components/molecules/reportFollowModal" ), { ssr: false } );

const component = props => {
	const {
		getDonationSrc, // own profile
		getSrc, // share other profile
		handleClick,
		handleClickAway, // share
		handleClickOpen, // share
		isModerator,
		toggleErrorModal,
		toggleModeratorOption,
		handleRedirect,
		isFollowingUser,
		shareModalOpen, // share
		shareAnchorEl, // share
		userId,
		isLoggedIn,
		userProfile,
		followerCount,
		isOwnProfile,
		toggleDialogInfoBox,
		handleGAEvent,
		shortBio
	} = props;

	const { classes } = useStyles ( undefined, { name: "styles" } );
	const { t }       = useTranslation ();

	const deviceType = useGetDeviceType ();
	// const isTablet       = deviceType === "tablet";
	const isMobile = deviceType === "mobile";

	const userProfileImage = (
		<div
			className = { classes.profileHeaderAvatarContainer }
			onClick   = { handleRedirect }
		>
			<Image
				className = { userProfile.isLive ? classes.actorAvatarActive : classes.actorAvatar }
				src       = { userProfile.photo }
				type      = "xhdpi"
			/>

			{
				userProfile.isLive ? (
					<div className = { classes.liveButton }>
						<span className = { classes.liveText }>LIVE</span>
					</div>
				) : null
			}
		</div>
	);

	// tier id 1 is for pro and 2 is for mid tier
	const isProCreator = userProfile.creatorTier?.tier_id === 1 || false;
	const isProUser    = userProfile.userRoles?.some ( element => {
		if ( element.typeId === 1 ) // typeId 1 is for pro
			return true;
	} );

	const creatorNameAndProfileIcons = (
		<div className = { classes.leftSideWrapper }>
			<Typography
				noWrap
				className = { classes.headerName }
				size      = "lg"
				title     = { userProfile.name }
			/>

			{
				userProfile.isCeleb === 1 ? (
					<Icon
						sameInBothTheme
						className = { classes.profileBadge }
						name      = "profileVerifyFilled"
					/>
				)
					: null
			}

			{
				isProUser ? (

					<HoverCard
						classNames = { { dropdown: classes.proHexagonDropdown } }
						offset     = { -8 }
						openDelay  = { 200 }
						position   = "bottom"
						// zIndex = { 9999 }
					>
						<HoverCard.Target>
							<Flex align = "center"
								justify    = "center"
							>
								<Icon sameInBothTheme
									className = { classes.profileBadge }
									name      = "proShineHexagon"
								/>
							</Flex>
						</HoverCard.Target>

						<HoverCard.Dropdown>
							<ProChip
								showShine = { false }
								title     = "Pro User"
							/>
						</HoverCard.Dropdown>
					</HoverCard>
				) : null
			}

			{
				!!( userProfile.discord || userProfile.instagram || userProfile.youtube ) && (
					<SocialMediaButtons
						discordHandle   = { userProfile.discord }
						instagramHandle = { userProfile.instagram }
						youtubeHandle   = { userProfile.youtube }
					/>
				)
			}
		</div>
	);

	const cityAndDateOfJoining = (
		<div className = { classes.headerLocationContainer }>
			{( userProfile?.country && userProfile?.city ) && (
				<div className = { classes.subDetailsContainer }>
					<Icon
						sameInBothTheme
						className = { classes.svgIconLocation }
						name      = "location"
					/>

					<Typography
						truncate
						color     = "secondary"
						component = { "p" }
						size      = "sm"
						title     = { `${ userProfile.city ? userProfile.city + "," : "" } ${ userProfile.country && userProfile.country.name ? userProfile.country.name : ""
						} ` }
					/>
				</div>
			)}

			<div className = { classes.subDetailsContainer }>
				<Icon
					sameInBothTheme
					className = { classes.svgIconCalendar }
					name      = "calender"
				/>

				<Typography
					truncate
					color     = "secondary"
					component = { "p" }
					size      = "sm"
					title     = { `Joined ${ dayjs ( userProfile.createdAt ).format ( "MMM, YYYY" ) }` }
				/>
			</div>
		</div>
	);

	const followerAndFollowingCount = (
		<div className = { classes.headerFollowerContainer }>
			<div
				onClick = { () => {
					toggleDialogInfoBox ( "0" );
					storeEvent ( "Profile", "Click", "Followers" );
				} }
				style   = { { marginTop: "0px", cursor: "pointer" } }
			>
				<Typography
					className = { classes.followerCountNumber }
					size      = "sm"
					title     = { shortenNumber ( followerCount ) }
				/>

				<Typography
					className = { classes.followerCountTag }
					color     = "secondary"
					size      = "sm"
					title     = { t ( "profile_followers" ) }
				/>
			</div>

			<div
				onClick = { () => {
					toggleDialogInfoBox ( "1" ); storeEvent ( "Profile", "Click", "Following" );
				} }
				style   = { { marginTop: "0px", marginLeft: "15px", cursor: "pointer" } }
			>
				<Typography
					className = { classes.followerCountNumber }
					size      = "sm"
					// color = "secondary"
					title     = { shortenNumber ( userProfile?.follow?.following ) }
				/>

				<Typography
					className = { classes.followerCountTag }
					color     = "secondary"
					size      = "sm"
					title     = { t ( "profile_following" ) }
				/>
			</div>
		</div>
	);

	const followBtn = ( !isLoggedIn || !isOwnProfile ) && (
		<FollowUser
			followComponent   = { (
				<PrimaryButton
					fullWidth
					className = { classes.followBtn }
					title     = { "+ Follow" }
				/>
			) }
			handleGAEvent     = { handleGAEvent }
			isFollowing       = { isFollowingUser }
			unfollowComponent = { (
				<SecondaryButton
					//
					fullWidth
					className = { classes.followBtn }
					title     = { "Following" }
				/>
			) }
			userId            = { Number ( userId ) }
			userName          = { userProfile.name }
		/>
	);

	const viewDetailedStats = ( isLoggedIn && isOwnProfile ) && (
		<PrimaryButton
			onClick = { handleClick }
			size    = "small"
			title   = { "View Detailed Stats" }

		/>
	);

	const shortBioText = userProfile?.shortBio && (
		<Typography
			className = { classes.profileHeaderBio }
			noWrap    = { isMobile }
			title     = { userProfile?.shortBio }
		/>
	);

	const [ proChannelsOpened, setProChannelsOpen ] = useState ( false );

	const router = useRouter ();

	function handleGameClick ( data ) {
		const slugifyName = convertToSlug ( data.name );

		storeEvent ( "Game_Card", "Clicked", `Game_Card_${ data.name }` );
		router.push ( `/game/${ data.id }/${ slugifyName }` );
	}

	const gamesChips = (
		<div className = { classes.profileHeaderMiddleBottom }>
			{
				isProCreator ? (
					<ProChannelDropdown
						from            = { "profile" }
						popoverOpened   = { proChannelsOpened }
						setPopover      = { setProChannelsOpen }
						TargetComponent = { () => {
							return (
								<ProChip
									onClick   = { () => {
										setProChannelsOpen ( open => !open );
									} }
									showShine = { true }
									title     = "Pro Creator"
								/>
							);
						} }
					/>

				) : null
			}

			{userProfile?.followingGames?.map ( game => {
				return (
					<Chip
						key          = { game?.id }
						buttonStyles = { classes.chipStyles }
						id           = { game?.id }
						image        = { game?.image }
						name         = { game?.name }
						onClick      = { () => handleGameClick ( game ) }
					/>
				);
			} )}
		</div>
	);

	const reportFollowModal = isLoggedIn && (
		<ReportFollowModal
			from                  = "profile-header"
			getDonationSrc        = { getDonationSrc }
			getSrc                = { getSrc }
			handleClickAway       = { handleClickAway }
			handleShareClickOpen  = { handleClickOpen }
			isFollowing           = { userProfile?.isFollowing?.following }
			isModerator           = { isModerator }
			onClickShareModal     = { () => { } }
			shareAnchorEl         = { shareAnchorEl }
			shareModalOpen        = { shareModalOpen }
			toggleDialogInfoBox   = { toggleDialogInfoBox }
			toggleErrorModal      = { toggleErrorModal }
			toggleModeratorOption = { toggleModeratorOption }
			userId                = { userProfile.id }
			userName              = { userProfile.name }
			userProfile           = { userProfile }
		/>
	);

	if ( isMobile ) {
		return (
			<div className = { classes.mobileContainer }>
				<div className = { classes.mobileProfileAndInfoContainer }>
					<div className = { classes.mobileImageContainer }>
						{userProfileImage}
					</div>

					<div className = { classes.mobileUserInfoContainer }>
						{creatorNameAndProfileIcons}

						{cityAndDateOfJoining}

						{followerAndFollowingCount}

					</div>

					<div>
						{reportFollowModal}
					</div>
				</div>

				{shortBioText}

				{gamesChips}

				{followBtn}

			</div>
		);
	}

	return (
		<div className = { classes.flex }>
			<div className = { classes.profileHeaderDetails }>
				{userProfileImage}

				<div className = { classes.profileHeaderContentContainer }>
					<div className = { classes.headerNameContainer }>

						{creatorNameAndProfileIcons}

						<div className = { classes.rightSideWrapper }>

							{followBtn}

							{viewDetailedStats}

							<div className = { classes.threeDotModal }>

								{reportFollowModal}
							</div>
						</div>

					</div>

					{cityAndDateOfJoining}

					{followerAndFollowingCount}

				</div>

			</div>

			{shortBioText}

			{gamesChips}
		</div>
	);
};

export default component;
